import { useMemo } from "react";
import { Clock, ListChecks } from "@phosphor-icons/react";
import { useSyncStatus } from "../hooks/useSyncStatus";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import ButtonNew from "@/components/buttonNew/buttonNew";

interface SyncStatusIndicatorProps {
  onViewChanges?: () => void;
}

/**
 * Sync Status Indicator Component
 *
 * Displays the current sync status of building financials with appropriate icons and messages.
 * Shows different states with color-coded icons and text.
 *
 * States:
 * - Success: Green check icon with "Laatste sync: X uur geleden"
 * - Failed: Red warning icon with error message
 * - Partial: Orange warning icon with partial success message
 * - Stale: Yellow clock icon with stale data warning
 * - Running: Blue spinner icon with "Sync wordt uitgevoerd..."
 *
 * Features:
 * - Auto-refreshes every 5 minutes
 * - Tooltip with detailed sync information
 * - Smooth transitions between states
 * - Loading skeleton for initial state
 * - Button to view sync changes
 *
 * @example
 * ```tsx
 * <SyncStatusIndicator onViewChanges={() => setChangesModalOpen(true)} />
 * ```
 */
export const SyncStatusIndicator = ({
  onViewChanges,
}: SyncStatusIndicatorProps) => {
  const { syncStatus, isLoading } = useSyncStatus();

  // Format last sync timestamp for main display
  const lastSyncDisplay = useMemo(() => {
    if (!syncStatus?.lastSyncCompleted) return "Nog niet gesynchroniseerd";

    const date = new Date(syncStatus.lastSyncCompleted);
    return date.toLocaleString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Brussels",
    });
  }, [syncStatus?.lastSyncCompleted]);

  // Determine text color based on status
  const textColor = useMemo(() => {
    if (!syncStatus) return "text-neutral-secondary-mid";

    if (syncStatus.status === "failed") return "text-brick-primary";
    if (syncStatus.status === "partial") return "text-mustard-primary";
    if (syncStatus.isStale) return "text-mustard-primary";
    if (syncStatus.status === "running") return "text-sky-primary";
    return "text-moss-primary";
  }, [syncStatus]);

  // Format duration for tooltip
  const formattedDuration = useMemo(() => {
    if (!syncStatus?.lastSyncDuration) return "Onbekend";
    const seconds = Math.round(syncStatus.lastSyncDuration / 1000);
    if (seconds < 60) return `${seconds} seconden`;
    const minutes = Math.round(seconds / 60);
    return `${minutes} ${minutes === 1 ? "minuut" : "minuten"}`;
  }, [syncStatus?.lastSyncDuration]);

  // Format timestamp for tooltip
  const formattedTimestamp = useMemo(() => {
    if (!syncStatus?.lastSyncCompleted) return "Nooit";
    const date = new Date(syncStatus.lastSyncCompleted);
    return date.toLocaleString("nl-NL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [syncStatus?.lastSyncCompleted]);

  // Show loading skeleton
  if (isLoading) {
    return (
      <div className="flex animate-pulse items-center gap-2">
        <div className="bg-neutral-stroke h-5 w-5 rounded-full" />
        <div className="bg-neutral-stroke h-4 w-32 rounded" />
      </div>
    );
  }

  // Don't render if no sync status
  if (!syncStatus) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex cursor-help items-center gap-2 transition-all duration-300 ease-in-out">
            <Clock
              size={16}
              weight="duotone"
              className="text-neutral-secondary-mid"
            />
            <span className="text-neutral-secondary hidden text-sm sm:inline">
              Laatste sync: {lastSyncDisplay}
            </span>
            <span className="text-neutral-secondary text-sm sm:hidden">
              {lastSyncDisplay}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="border-neutral-stroke max-w-xs border bg-white p-4 shadow-lg">
          <div className="flex flex-col gap-3">
            <div className="border-neutral-stroke border-b pb-2 text-sm font-semibold text-gray-900">
              Sync details
            </div>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-600">Status:</span>
                <span className={`font-semibold ${textColor}`}>
                  {syncStatus.status === "completed" && "Voltooid"}
                  {syncStatus.status === "failed" && "Gefaald"}
                  {syncStatus.status === "partial" && "Gedeeltelijk"}
                  {syncStatus.status === "running" && "Bezig..."}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-600">Laatste sync:</span>
                <span className="font-semibold text-gray-900">
                  {formattedTimestamp}
                </span>
              </div>
              {syncStatus.lastSyncDuration !== null && (
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-600">Duur:</span>
                  <span className="font-semibold text-gray-900">
                    {formattedDuration}
                  </span>
                </div>
              )}
              {syncStatus.buildingsProcessed !== null && (
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-600">
                    Gebouwen verwerkt:
                  </span>
                  <span className="font-semibold text-gray-900">
                    {syncStatus.buildingsProcessed}
                  </span>
                </div>
              )}
            </div>

            {/* View Changes Button - Always show if sync completed successfully */}
            {onViewChanges &&
              (syncStatus.status === "completed" ||
                syncStatus.lastSyncCompleted) && (
                <ButtonNew
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewChanges();
                  }}
                  icon={<ListChecks size={16} className="mr-2" />}
                  label="Bekijk wijzigingen"
                  className="bg-sky-primary hover:bg-sky-primary-mid text-neutral mt-2 inline-flex h-8 w-full items-center justify-center gap-2 rounded-md border-0 px-3 text-xs font-medium transition-colors"
                />
              )}

            {syncStatus.errorMessage && (
              <div className="border-neutral-stroke mt-2 flex flex-col gap-1 border-t pt-2">
                <span className="text-sm font-medium text-gray-600">
                  Foutmelding:
                </span>
                <span className="text-brick-primary text-sm font-medium break-words">
                  {syncStatus.errorMessage}
                </span>
              </div>
            )}
            {syncStatus.isStale && (
              <div className="border-neutral-stroke mt-2 flex flex-col gap-1 border-t pt-2">
                <span className="text-mustard-primary text-sm font-medium">
                  Data is mogelijk verouderd. Overweeg cache te wissen.
                </span>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
